import {
  CalendarDetailsVisibility,
  CalendarSetting,
  ExternalConnection,
  ExternalConnectionGoogleProviderInfo,
  ExternalConnectionGoogleSettings,
  ExternalConnectionsBrokenReasons,
  ExternalConnectionsCapabilities,
  ExternalConnectionsProviders,
  ExternalConnectionsStatuses,
  ExternalConnectionsTypes,
  ID,
  LinkedItemType,
  Roles,
  SyncMode,
  TaskCalendarOverride,
  Timestamp,
} from '@griply/types';

import { DocumentSnapshot } from '../types/firestore';
import { firebaseTimestampToDate } from './firebase-timestamp-to-date';

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const coerceOptionalIdArray = (value: unknown): ID[] | undefined => {
  if (!Array.isArray(value)) {
    return undefined;
  }
  return value.filter((item): item is ID => typeof item === 'string');
};

const coerceIdArray = (value: unknown): ID[] =>
  Array.isArray(value)
    ? value.filter((item): item is ID => typeof item === 'string')
    : [];

const coerceDetailsVisibility = (
  value: unknown,
): CalendarDetailsVisibility | undefined => {
  if (value === undefined || value === null) {
    return undefined;
  }
  return Object.values(CalendarDetailsVisibility).includes(
    value as CalendarDetailsVisibility,
  )
    ? (value as CalendarDetailsVisibility)
    : CalendarDetailsVisibility.Hidden;
};

const coerceCalendarSetting = (value: unknown): CalendarSetting | null => {
  if (!isPlainObject(value)) {
    return null;
  }
  if (
    !Object.values(LinkedItemType).includes(
      value.linkedItemType as LinkedItemType,
    )
  ) {
    return null;
  }
  if (typeof value.calendarId !== 'string') {
    return null;
  }
  const mode = Object.values(SyncMode).includes(value.mode as SyncMode)
    ? (value.mode as SyncMode)
    : SyncMode.Manual;
  const detailsVisibility = coerceDetailsVisibility(value.detailsVisibility);
  const syncTasksFrom = firebaseTimestampToDate(
    value.syncTasksFrom as Date,
  ) as Timestamp | null;

  return {
    linkedItemType: value.linkedItemType as LinkedItemType,
    calendarId: value.calendarId,
    mode,
    ...(detailsVisibility !== undefined && { detailsVisibility }),
    ...(syncTasksFrom && { syncTasksFrom }),
  };
};

const coerceCalendarSettings = (
  value: unknown,
): Record<ID, CalendarSetting> => {
  if (!isPlainObject(value)) {
    return {};
  }
  return Object.entries(value).reduce<Record<ID, CalendarSetting>>(
    (acc, [key, raw]) => {
      const setting = coerceCalendarSetting(raw);
      if (setting) {
        acc[key as ID] = setting;
      }
      return acc;
    },
    {},
  );
};

const coerceTaskOverride = (value: unknown): TaskCalendarOverride | null => {
  if (typeof value === 'boolean') {
    return { synced: value };
  }
  if (!isPlainObject(value)) {
    return null;
  }
  const synced = typeof value.synced === 'boolean' ? value.synced : undefined;
  const detailsVisibility = coerceDetailsVisibility(value.detailsVisibility);

  if (synced === undefined && detailsVisibility === undefined) {
    return null;
  }

  return {
    ...(synced !== undefined && { synced }),
    ...(detailsVisibility !== undefined && { detailsVisibility }),
  };
};

const coerceTaskOverrides = (
  value: unknown,
): Record<ID, TaskCalendarOverride> => {
  if (!isPlainObject(value)) {
    return {};
  }
  return Object.entries(value).reduce<Record<ID, TaskCalendarOverride>>(
    (acc, [key, raw]) => {
      const override = coerceTaskOverride(raw);
      if (override) {
        acc[key as ID] = override;
      }
      return acc;
    },
    {},
  );
};

const coerceSettings = (
  value: unknown,
): ExternalConnectionGoogleSettings | null => {
  if (!isPlainObject(value)) {
    return null;
  }
  return {
    calendarBlacklist: coerceOptionalIdArray(value.calendarBlacklist),
    syncedGoals: coerceIdArray(value.syncedGoals),
    syncedLifeAreas: coerceIdArray(value.syncedLifeAreas),
    syncAllTasks: coerceBoolean(value.syncAllTasks),
    calendarSettings: coerceCalendarSettings(value.calendarSettings),
    taskOverrides: coerceTaskOverrides(value.taskOverrides),
  };
};

const coerceBoolean = (value: unknown): boolean | undefined =>
  typeof value === 'boolean' ? value : undefined;

const coerceProvider = (value: unknown): ExternalConnectionsProviders =>
  Object.values(ExternalConnectionsProviders).includes(
    value as ExternalConnectionsProviders,
  )
    ? (value as ExternalConnectionsProviders)
    : ExternalConnectionsProviders.Google;

const coerceType = (value: unknown): ExternalConnectionsTypes =>
  Object.values(ExternalConnectionsTypes).includes(
    value as ExternalConnectionsTypes,
  )
    ? (value as ExternalConnectionsTypes)
    : ExternalConnectionsTypes.Calendar;

// Absent stays absent: it distinguishes a legacy connection (no capability tracking)
// from a modern one that was granted nothing.
const coerceCapabilities = (
  value: unknown,
): ExternalConnectionsCapabilities[] | undefined => {
  if (!Array.isArray(value)) {
    return undefined;
  }
  return value.filter((item): item is ExternalConnectionsCapabilities =>
    Object.values(ExternalConnectionsCapabilities).includes(
      item as ExternalConnectionsCapabilities,
    ),
  );
};

// An unrecognised status must not read as broken — a future backend state would
// otherwise show every connection as needing a reconnect.
const coerceStatus = (
  value: unknown,
): ExternalConnectionsStatuses | undefined =>
  Object.values(ExternalConnectionsStatuses).includes(
    value as ExternalConnectionsStatuses,
  )
    ? (value as ExternalConnectionsStatuses)
    : undefined;

const coerceBrokenReason = (
  value: unknown,
): ExternalConnectionsBrokenReasons | undefined =>
  Object.values(ExternalConnectionsBrokenReasons).includes(
    value as ExternalConnectionsBrokenReasons,
  )
    ? (value as ExternalConnectionsBrokenReasons)
    : undefined;

export const externalConnectionConverter = {
  fromFirestore: (snapshot: DocumentSnapshot): ExternalConnection => {
    const data = snapshot.data() as Record<string, unknown>;

    return {
      id: data.id as ID,
      provider: coerceProvider(data.provider),
      providerInfo: data.providerInfo as ExternalConnectionGoogleProviderInfo,
      roles: data.roles as Roles,
      type: coerceType(data.type),
      settings: coerceSettings(data.settings),
      capabilities: coerceCapabilities(data.capabilities),
      status: coerceStatus(data.status),
      brokenReason: coerceBrokenReason(data.brokenReason),
      createdAt: firebaseTimestampToDate(data.createdAt as Date) as Timestamp,
      updatedAt: firebaseTimestampToDate(data.updatedAt as Date) as Timestamp,
      lastUsedAt: firebaseTimestampToDate(data.lastUsedAt as Date) as Timestamp,
    };
  },
  toFirestore: (externalConnection: ExternalConnection) => externalConnection,
};
